import React, { useState, useEffect } from 'react';
import {
  Container, Header, Table, Button, Link, StatusIndicator, Box, SpaceBetween
} from '@cloudscape-design/components';
import { imageStudioApi } from './imageStudioApi.js';

const OP_ICONS = {
  resize: '🖼️', background: '✂️', convert: '🔄', enhance: '✨', lifestyle: '📸',
};

export default function ResultsSection({ refreshTrigger, selectedRunId, onRunSelect, onRunsLoaded }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadRuns = async () => {
    setLoading(true);
    try {
      const data = await imageStudioApi.listRuns();
      const runsList = data.runs || [];
      setRuns(runsList);
      if (onRunsLoaded) onRunsLoaded(runsList);
    } catch (err) { console.error('Failed to load runs:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRuns(); }, [refreshTrigger]);

  const handleDownload = async (runId, operation) => {
    setDownloadingId(runId);
    try { await imageStudioApi.downloadCsv(runId, operation); }
    catch (err) { console.error('CSV download failed:', err); }
    finally { setDownloadingId(null); }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
      + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // Duration column — shows elapsed time from started_at to completed_at.
  // Mirrors the IC File Recent Jobs table format ("3s" / "2m 14s").
  // Falls back to em-dash when either endpoint is missing (older runs from
  // before 3 Jun 2026, or runs still in `processing` state).
  const formatDuration = (item) => {
    const start = item.started_at || item.timestamp;
    const end = item.completed_at;
    if (!start || !end) return '—';
    const diffMs = new Date(end) - new Date(start);
    if (!Number.isFinite(diffMs) || diffMs < 0) return '—';
    const totalSec = Math.round(diffMs / 1000);
    if (totalSec < 1) return '<1s';
    if (totalSec < 60) return `${totalSec}s`;
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${mins}m ${secs}s`;
  };

  const columnDefinitions = [
    {
      id: 'select', header: '',
      cell: (item) => {
        const isSelected = item.run_id === selectedRunId;
        const icon = OP_ICONS[item.operation] || '•';
        return (
          <button
            className={`run-bubble ${isSelected ? 'active' : ''}`}
            onClick={() => onRunSelect && onRunSelect(item.run_id, item.operation)}
            title={`Preview this run (${item.run_id})`}
          >
            <span className="run-bubble-icon">{icon}</span>
            <span className="run-bubble-label">{isSelected ? 'Viewing' : 'Preview'}</span>
          </button>
        );
      },
      width: 110
    },
    { id: 'timestamp', header: 'Time', cell: (item) => formatTime(item.timestamp), width: 120 },
    { id: 'duration', header: 'Duration', cell: formatDuration, width: 90 },
    { id: 'user', header: 'User', cell: (item) => item.user || 'unknown', width: 90 },
    { id: 'operation', header: 'Tool', cell: (item) => {
      const op = item.operation || 'resize';
      const labels = { resize: '🖼️ Resize', background: '✂️ Background', convert: '🔄 Convert', enhance: '✨ Enhance', lifestyle: '📸 Lifestyle' };
      return labels[op] || op;
    }, width: 120 },
    { id: 'config', header: 'Config', cell: (item) => {
      return `${item.ratio || '-'} / ${item.method || '-'}`;
    }, width: 160 },
    {
      id: 'status', header: 'Status',
      // Check item.status FIRST — before today (28 Jul 2026) this column
      // only ever looked at success_count/failed_count/total_images and
      // never read the `status`/`error` fields metadata.json already
      // writes. A worker crash (OOM, uncaught exception, etc.) writes
      // status:"failed" + total_images:0 + an error message, but the old
      // logic fell through to the "N of N processed" branch and rendered
      // the uninformative "0 of 0 processed" — no indication anything
      // went wrong, no error message, no distinction from a genuinely
      // empty-but-successful run. Still-running jobs (status:"processing")
      // had the same problem, showing a stale placeholder count instead of
      // "still running".
      //
      // Error text renders in a hover tooltip, not inline in the cell —
      // reuses the exact position:fixed + JS-measured-offset pattern from
      // listing-creation.css's `.listing-error-trigger` / `.listing-error-
      // tooltip` (see ListingCreation.jsx's Recent Jobs "Download" column).
      // That pattern was chosen deliberately: earlier tooltip attempts in
      // this codebase used CSS-only `position: absolute`, which Cloudscape
      // Table clips via its row `overflow: hidden` — the tooltip either got
      // cut off or rendered behind the next row. `position: fixed` escapes
      // that clipping entirely; the onMouseEnter handler measures the
      // trigger's real screen position via getBoundingClientRect() and
      // sets top/left on the tooltip element directly, so it's not
      // relying on any CSS containing-block relationship the table could
      // interfere with.
      cell: (item) => {
        if (item.status === 'failed') {
          return (
            <span
              className="studio-status-error-trigger"
              onMouseEnter={(e) => {
                const tip = e.currentTarget.querySelector('.studio-status-error-tooltip');
                if (tip) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  tip.style.top = `${rect.bottom + 4}px`;
                  tip.style.left = `${rect.left}px`;
                }
              }}
            >
              <StatusIndicator type="error">Failed</StatusIndicator>
              <span className="studio-status-error-tooltip" role="tooltip">
                {item.error || 'No error details available.'}
              </span>
            </span>
          );
        }
        if (item.status === 'processing') {
          return <StatusIndicator type="in-progress">Processing…</StatusIndicator>;
        }
        return item.failed_count > 0
          ? <SpaceBetween direction="horizontal" size="xxs">
              <StatusIndicator type="success">{item.success_count} ok</StatusIndicator>
              <StatusIndicator type="error">{item.failed_count} failed</StatusIndicator>
            </SpaceBetween>
          : <StatusIndicator type="success">{item.success_count} of {item.total_images} processed</StatusIndicator>;
      },
      width: 200
    },
    {
      id: 'actions', header: 'CSV',
      cell: (item) => <Button variant="inline-link" iconName="download"
        loading={downloadingId === item.run_id} onClick={() => handleDownload(item.run_id, item.operation)}>CSV</Button>,
      width: 80
    }
  ];

  return (
    <Container header={
      <Header variant="h2"
        description="Click 'Preview' on any row to view its images above"
        actions={<Button iconName="refresh" onClick={loadRuns} loading={loading}>Refresh</Button>}>
        📋 Processing History
      </Header>
    }>
      <Table columnDefinitions={columnDefinitions} items={runs} loading={loading}
        loadingText="Loading..." variant="embedded" sortingDisabled
        empty={
          <Box textAlign="center" color="inherit" padding="l">
            <SpaceBetween size="xxs">
              <Box variant="p" color="inherit"><b>No processing history yet</b></Box>
              <Box variant="p" color="text-body-secondary">Process images to see history here.</Box>
            </SpaceBetween>
          </Box>
        }
      />
    </Container>
  );
}
